(() => {
  'use strict';

  const ROOT = '[data-luggex-checkout-safety-root]';
  const ACTIONS = '[data-luggex-checkout-safety-actions]';
  const NORMAL = 'a.xcotton-checkout-without';
  const PAID = 'button.xcotton-checkout[data-xmh-checkout="true"]';
  const FALLBACK = '[data-luggex-native-checkout-fallback]';
  const STATUS = '[data-luggex-checkout-safety-status]';
  const ERROR = '[data-luggex-checkout-safety-error]';
  const RETRY = '[data-luggex-checkout-safety-retry]';
  const KEY = '__luggexCheckoutSafety';
  const failError = (code) => Object.assign(new Error(code), { code });
  const identifier = (value) => {
    const text = String(value ?? '').replace(/^gid:\/\/shopify\/(?:Product|ProductVariant)\//, '');
    if (!/^[1-9]\d*$/.test(text)) throw failError('config_error');
    return text;
  };
  const stable = (value) => {
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
    return `{${Object.keys(value).sort().map((name) => `${JSON.stringify(name)}:${stable(value[name])}`).join(',')}}`;
  };
  const cartSnapshot = (raw) => {
    if (!raw || !Array.isArray(raw.items) || !/^[A-Z]{3}$/.test(raw.currency || '') || !Number.isSafeInteger(raw.total_price) || raw.total_price < 0) throw failError('cart_changed');
    const keys = new Set();
    const lines = raw.items.map((item) => {
      if (!item || typeof item.key !== 'string' || !item.key || keys.has(item.key) || !Number.isSafeInteger(item.quantity) || item.quantity < 0) throw failError('cart_changed');
      keys.add(item.key);
      const plan = item.selling_plan_allocation?.selling_plan?.id;
      const properties = item.properties == null ? {} : item.properties;
      if (typeof properties !== 'object' || Array.isArray(properties)) throw failError('cart_changed');
      return { key: item.key, productId: identifier(item.product_id), variantId: identifier(item.variant_id ?? item.id), quantity: item.quantity,
        plan: plan == null ? '' : identifier(plan), properties: stable(properties), price: item.price, finalLinePrice: item.final_line_price };
    });
    const selection = String(raw.attributes?.xcsp_confirm ?? '');
    return { currency: raw.currency, total: raw.total_price, lines, confirm: selection === '0' || selection === '1' ? selection : '' };
  };
  const goodsSignature = (cart, insuranceId) => {
    const groups = new Map();
    cart.lines.filter((line) => line.productId !== insuranceId && line.quantity > 0).forEach((line) => {
      const key = stable([line.productId, line.variantId, line.plan, line.properties]);
      groups.set(key, (groups.get(key) || 0) + line.quantity);
    });
    return stable([...groups.entries()].sort((a, b) => a[0].localeCompare(b[0])));
  };
  const insuranceLines = (cart, productId) => cart.lines.filter((line) => line.productId === productId && line.quantity > 0);
  const parseDisplayedQuote = (text, currency) => {
    // This format is confirmed on the current USD widget. Other presentment
    // formats require their own observed contract; never guess separators.
    if (currency !== 'USD') throw failError('cart_changed');
    const normalized = String(text || '').trim();
    if (!/^\$(?:0|[1-9]\d{0,2}(?:,\d{3})*|[1-9]\d*)\.\d{2}$/.test(normalized)) throw failError('cart_changed');
    const [major, minor] = normalized.slice(1).replace(/,/g, '').split('.');
    const amount = Number(BigInt(major) * 100n + BigInt(minor));
    if (!Number.isSafeInteger(amount) || amount <= 0 || new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100) !== normalized) throw failError('cart_changed');
    return { amount, currency, text: normalized };
  };
  const quoteText = (control) => {
    const nodes = control?.querySelectorAll('span.money.xcotton-money') || [];
    if (nodes.length !== 1) throw failError('cart_changed');
    return nodes[0].textContent.trim();
  };
  const validateConfig = (raw, origin) => {
    if (!raw || typeof raw !== 'object') throw failError('config_error');
    const insuranceId = identifier(raw.insuranceProductId);
    const urls = {};
    for (const [name, suffix] of [['cartUrl', '/cart'], ['cartJsonUrl', '/cart.js'], ['updateUrl', '/cart/update.js']]) {
      if (typeof raw[name] !== 'string' || !raw[name]) throw failError('config_error');
      const url = new URL(raw[name], origin);
      const validPath = name === 'cartJsonUrl' ? /\/cart\.(?:js|json)$/.test(url.pathname) : url.pathname.endsWith(suffix);
      if (url.origin !== origin || url.search || url.hash || !validPath) throw failError('config_error');
      urls[name] = url.href;
    }
    const variantIds = new Set();
    if (Array.isArray(raw.insuranceVariants)) for (const variant of raw.insuranceVariants) variantIds.add(identifier(variant.id));
    if (raw.currency != null && !/^[A-Z]{3}$/.test(raw.currency)) throw failError('config_error');
    return { insuranceId, variantIds, currency: raw.currency || '', ...urls, messages: raw.messages && typeof raw.messages === 'object' ? raw.messages : {} };
  };

  function createRuntime(root, options = {}) {
    const document = root.document;
    const clock = () => root.Date?.now?.() ?? Date.now();
    const requestMs = options.requestMs || 6000;
    const sdkMs = options.sdkMs || 15000;
    const settleMs = options.settleMs || 4000;
    const navMs = options.navMs || 6000;
    let alive = true;
    let suspended = false;
    let active;
    let config;
    const listeners = [];
    const ownLocks = new Map();
    const enabled = (scope) => scope?.getAttribute('data-luggex-checkout-safety-enabled') !== 'false';
    const roots = () => [...document.querySelectorAll(ROOT)].filter((scope) => scope.isConnected && enabled(scope));
    const text = (code) => config?.messages?.[code] || ({ config_error: 'Checkout is unavailable right now. Reload the cart to retry.', cart_changed: 'Your cart changed. Reload the cart to retry safely.', timeout: 'Checkout could not be confirmed. Reload the cart to retry.', navigation_failed: 'Checkout did not open. Reload the cart to retry.' }[code] || 'Checkout could not be confirmed. Reload the cart to retry.');
    const currentRoot = (intent) => {
      const candidates = roots().filter((scope) => scope.getAttribute('data-luggex-checkout-safety-surface') === intent.surface);
      if (candidates.length !== 1) throw failError('cart_changed');
      return candidates[0];
    };
    const nativeIn = (scope) => {
      const actions = scope.querySelector(ACTIONS);
      const candidates = [...(actions?.querySelectorAll('[name="checkout"]') || [])].filter((node) => ['BUTTON', 'INPUT'].includes(node.tagName) && node.getAttribute('type') === 'submit' && !node.hasAttribute('data-xmh-checkout'));
      if (candidates.length !== 1) throw failError('config_error');
      return candidates[0];
    };
    const formOf = (submitter) => submitter?.form || (submitter?.getAttribute('form') ? document.getElementById(submitter.getAttribute('form')) : submitter?.closest('form'));
    const containerOf = (scope) => scope.closest('main-cart') || scope.closest('cart-drawer') || scope.closest('.shopify-section');
    const quantityInputs = (scope) => {
      const container = containerOf(scope);
      if (!container) throw failError('cart_changed');
      return [...container.querySelectorAll('[name="updates[]"]')];
    };
    const quantitiesMatch = (scope, cart, intent) => {
      const container = containerOf(scope);
      const reason = (value) => { if (intent) intent.qtyState = value; return false; };
      if (!container) return reason('container_missing');
      if ([...container.querySelectorAll('cart-items')].some((node) => node.classList.contains('pointer-events-none') || node.getAttribute('aria-busy') === 'true')) return reason('pending');
      const entries = new Map(cart.lines.map((line) => [line.key, line]));
      const seen = new Set();
      for (const input of quantityInputs(scope)) {
        if (input.getAttribute('type') === 'hidden' && !input.hasAttribute('data-index')) continue;
        const key = input.getAttribute('data-index');
        const line = entries.get(key);
        if (!line && (intent?.mode === 'normal' || intent?.cleanedUnconfirmed) && intent.knownInsuranceKeys?.has(key)) continue;
        if (!line) return reason('line_missing');
        if (!/^\d+$/.test(String(input.value)) || Number(input.value) !== line.quantity) return reason('value_mismatch');
        seen.add(key);
      }
      if (!cart.lines.filter((line) => line.productId !== config.insuranceId && line.quantity > 0).every((line) => seen.has(line.key))) return reason('ordinary_line_unseen');
      if (intent) intent.qtyState = 'matching';
      return true;
    };
    const live = (intent) => alive && !suspended && active === intent && !['FAILED', 'EXITED', 'LEAVING'].includes(intent.phase);
    const assertLive = (intent) => { if (!live(intent)) throw failError('timeout'); };
    const restoreUpdates = (intent) => {
      for (const [input, previous] of intent.disabledUpdates || []) {
        if (previous === null) input.removeAttribute('disabled'); else input.setAttribute('disabled', previous);
      }
      intent.disabledUpdates = [];
    };
    const annotateReviewIntent = (intent) => {
      if (String(root.Shopify?.theme?.id) !== '160325730459') return;
      const script = document.getElementById('luggex-native-benefit-review');
      if (!script || script.getAttribute('data-enabled') !== 'true' || !intent) return;
      for (const [key, value] of Object.entries({ phase: intent.phase, step: intent.lastStep || '', reads: intent.readCount || 0, writes: intent.writeCount || 0, error: intent.errorCode || '', request: intent.requestState || '', requestMs: intent.lastRequestMs || 0 })) {
        script.setAttribute('data-safety-' + key.replace(/[A-Z]/g, (letter) => '-' + letter.toLowerCase()), String(value));
      }
    };
    const render = (code, error = false) => {
      annotateReviewIntent(active);
      for (const scope of roots()) {
        const status = scope.querySelector(STATUS), failure = scope.querySelector(ERROR), retry = scope.querySelector(RETRY);
        if (status) { status.textContent = text(code); status.hidden = error; }
        if (failure) {
          const message = failure.querySelector('[data-luggex-checkout-safety-error-message], [data-error-message]');
          if (message) message.textContent = error ? text(code) : '';
          else if (!failure.querySelector(RETRY)) failure.textContent = error ? text(code) : '';
          failure.hidden = !error;
        }
        if (retry) retry.hidden = !error;
        scope.setAttribute('aria-busy', String(!error));
      }
    };
    const lockVisibleControls = () => {
      for (const scope of roots()) for (const control of scope.querySelectorAll(`${NORMAL}, ${PAID}, ${FALLBACK}`)) {
        if (!ownLocks.has(control)) ownLocks.set(control, { disabled: control.getAttribute('disabled'), aria: control.getAttribute('aria-disabled') });
        if (control.tagName === 'BUTTON' || control.tagName === 'INPUT') control.setAttribute('disabled', '');
        control.setAttribute('aria-disabled', 'true');
      }
    };
    const restoreLocks = () => {
      ownLocks.forEach((previous, control) => {
        if (previous.disabled === null) control.removeAttribute('disabled'); else control.setAttribute('disabled', previous.disabled);
        if (previous.aria === null) control.removeAttribute('aria-disabled'); else control.setAttribute('aria-disabled', previous.aria);
      }); ownLocks.clear();
    };
    const fail = (intent, code = 'error') => {
      if (active !== intent || intent.phase === 'FAILED' || suspended && intent.phase === 'EXITED') return;
      intent.errorPhase = intent.phase;
      intent.phase = 'FAILED';
      intent.errorCode = code;
      root.clearTimeout(intent.watchdog); root.clearTimeout(intent.navigationTimer);
      intent.controllers?.forEach((controller) => controller.abort());
      root.__luggexCheckoutSafetyBootstrap?.cancelPermit?.();
      restoreUpdates(intent);
      lockVisibleControls();
      render(code, true);
      if (intent.onFailure && intent.directResolved && !intent.failureNotified) { intent.failureNotified = true; try { intent.onFailure({ code, message: text(code), reloadRequired: true }); } catch (_) {} }
      // A failed/unknown intent remains the only intent in this document.
      // Abort is not proof that a server write did not happen; retry reloads.
    };
    const block = (event) => { event.preventDefault(); event.stopImmediatePropagation(); };
    const delay = (milliseconds, intent) => new Promise((resolve, reject) => root.setTimeout(() => { try { assertLive(intent); resolve(); } catch (error) { reject(error); } }, milliseconds));
    const bounded = async (promise, intent, milliseconds = requestMs) => {
      let timer;
      try {
        return await Promise.race([promise, new Promise((_, reject) => { timer = root.setTimeout(() => reject(failError('timeout')), milliseconds); })]);
      } finally { root.clearTimeout(timer); assertLive(intent); }
    };
    const request = async (url, intent, body) => {
      assertLive(intent);
      const started = clock();
      intent.requestStarted = started;
      if (body) intent.writeCount = (intent.writeCount || 0) + 1; else intent.readCount = (intent.readCount || 0) + 1;
      const controller = new root.AbortController(); intent.controllers.add(controller);
      intent.requestState = body ? 'write_pending' : 'read_pending'; annotateReviewIntent(intent);
      try {
        const response = await bounded(root.fetch(url, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: { Accept: 'application/json', ...(body && { 'Content-Type': 'application/json' }) }, ...(body && { body: JSON.stringify(body) }) }), intent);
        if (!response.ok) throw failError('error');
        const json = await bounded(response.json(), intent);
        return cartSnapshot(json);
      } catch (error) { controller.abort(); throw failError(error.code || 'error'); }
      finally { intent.controllers.delete(controller); intent.lastRequestMs = clock() - started; intent.requestState = 'settled'; annotateReviewIntent(intent); }
    };
    const verifyGoods = (intent, cart) => {
      if (cart.currency !== intent.baseline.currency || goodsSignature(cart, config.insuranceId) !== intent.goods) throw failError('cart_changed');
    };
    const settledCart = async (intent, expectedSignature) => {
      let end;
      let previous;
      intent.lastStep = expectedSignature ? 'final:quantity_settle' : 'baseline:quantity_settle';
      while (end === undefined || clock() < end) {
        assertLive(intent);
        const cart = await request(config.cartJsonUrl, intent);
        if (end === undefined) end = clock() + settleMs;
        if (expectedSignature) verifyGoods(intent, cart);
        const scope = currentRoot(intent);
        if (quantitiesMatch(scope, cart, intent)) {
          const signature = stable([cart.currency, cart.confirm, cart.lines.map((line) => [line.key, line.quantity, ...(line.productId === config.insuranceId ? [line.price, line.finalLinePrice] : [])])]);
          if (previous === signature) return cart;
          previous = signature;
        } else previous = undefined;
        await delay(350, intent);
      }
      throw failError('cart_changed');
    };
    const readPublicQuoteVariant = async (intent) => {
      const api = root.XCOTTON?.api;
      if (typeof api?.getSPProductVariantId !== 'function') throw failError('cart_changed');
      const result = await bounded(Promise.resolve().then(() => api.getSPProductVariantId()), intent);
      if (result == null) throw failError('cart_changed');
      const variantId = identifier(result);
      if (!config.variantIds.has(variantId)) throw failError('config_error');
      return variantId;
    };
    const commit = (intent, cart) => {
      assertLive(intent);
      intent.lastStep = 'commit:submit';
      const scope = currentRoot(intent), native = nativeIn(scope), form = formOf(native);
      if (!form || !form.isConnected || native.disabled || typeof form.requestSubmit !== 'function' || !quantitiesMatch(scope, cart, intent)) throw failError('cart_changed');
      if (form.checkValidity && !form.checkValidity()) { form.reportValidity?.(); throw failError('cart_changed'); }
      intent.disabledUpdates = [];
      const inputs = [...(form.elements || [])].filter((input) => input.getAttribute?.('name') === 'updates[]');
      const expectedConfirm = intent.mode === 'normal' ? '0' : '1';
      for (const field of form.elements || []) {
        if (field.disabled || field.getAttribute?.('name') !== 'attributes[xcsp_confirm]') continue;
        if (['checkbox', 'radio'].includes(field.getAttribute('type')) && !field.checked) continue;
        if (['BUTTON', 'INPUT'].includes(field.tagName) && ['button', 'submit', 'reset'].includes(field.getAttribute('type')) && field !== native) continue;
        if (String(field.value) !== expectedConfirm) { intent.lastStep = 'commit:selection_field'; throw failError('cart_changed'); }
      }
      const byKey = new Map(cart.lines.map((line) => [line.key, line]));
      for (const input of inputs) {
        if (input.getAttribute('type') === 'hidden' && !input.hasAttribute('data-index')) continue;
        const line = byKey.get(input.getAttribute('data-index'));
        if (!line && (intent.mode === 'normal' || intent.cleanedUnconfirmed) && intent.knownInsuranceKeys.has(input.getAttribute('data-index'))) continue;
        if (!line || Number(input.value) !== line.quantity) throw failError('cart_changed');
      }
      inputs.forEach((input) => { intent.disabledUpdates.push([input, input.getAttribute('disabled')]); input.setAttribute('disabled', ''); });
      intent.phase = 'COMMIT';
      intent.form = form; intent.submitter = native; intent.permitUsed = false;
      render('opening');
      root.__luggexCheckoutSafetyBootstrap?.allowOnce?.(form, native);
      form.requestSubmit(native);
      if (!intent.permitUsed) throw failError('navigation_failed');
    };
    const validateFinal = async (intent) => {
      if (!live(intent) || intent.checkingFinal) return;
      intent.checkingFinal = true; intent.phase = 'FINAL';
      try {
        intent.lastStep = 'final:read';
        let cart = await request(config.cartJsonUrl, intent); verifyGoods(intent, cart);
        if (intent.mode === 'normal') {
          render('clearing');
          intent.lastStep = 'normal:clear';
          const updates = Object.fromEntries(insuranceLines(cart, config.insuranceId).map((line) => { intent.knownInsuranceKeys.add(line.key); return [line.key, 0]; }));
          if (Object.keys(updates).length || cart.confirm !== '0') await request(config.updateUrl, intent, { updates, attributes: { xcsp_confirm: '0' } });
          cart = await settledCart(intent, true);
          if (insuranceLines(cart, config.insuranceId).length) throw failError('cart_changed');
          intent.lastStep = 'normal:confirm';
          if (cart.confirm !== '0') throw failError('cart_changed');
        } else {
          cart = await settledCart(intent, true);
          const insurance = insuranceLines(cart, config.insuranceId);
          intent.lastStep = 'paid:amount';
          if (insurance.length !== 1 || insurance[0].quantity !== 1 || insurance[0].variantId !== intent.quoteVariant || insurance[0].price !== intent.quote.amount || insurance[0].finalLinePrice !== intent.quote.amount) throw failError('cart_changed');
          const currentPaid = currentRoot(intent).querySelector(PAID);
          if (quoteText(currentPaid) !== intent.quote.text) throw failError('cart_changed');
          intent.lastStep = 'paid:confirm';
          if (cart.confirm !== '1') throw failError('cart_changed');
        }
        verifyGoods(intent, cart); commit(intent, cart);
      } catch (error) { fail(intent, error.code || 'error'); }
    };
    const checkpoint = (intent, event, sourceNative = intent.native) => {
      block(event);
      if (active !== intent || intent.phase !== 'SDK' || sourceNative !== intent.native) return;
      root.clearTimeout(intent.watchdog);
      void validateFinal(intent);
    };
    const bindNative = (intent, native) => {
      const nativeListener = (event) => checkpoint(intent, event, native);
      native.addEventListener('click', nativeListener, true);
      listeners.push(() => native.removeEventListener('click', nativeListener, true));
      intent.native = native;
    };
    const syncReviewBenefits = async (intent) => {
      // Cooperate only with the approved live/review native-benefit adapters.
      if (!['159621873819', '160325730459'].includes(String(root.Shopify?.theme?.id))) return false;
      const script = document.getElementById('luggex-native-benefit-review');
      if (!script || script.getAttribute('data-enabled') !== 'true') return false;
      const adapter = root.__luggexNativeBenefitReview;
      if (typeof adapter?.sync !== 'function' || typeof adapter?.refreshed !== 'function') throw failError('config_error');
      intent.lastStep = 'baseline:native_benefits';
      await bounded((async () => {
        const outcome = await adapter.sync();
        if (!['synced', 'prepared'].includes(outcome?.status)) throw failError('cart_changed');
        await adapter.refreshed();
      })(), intent, sdkMs);
      assertLive(intent);
      return true;
    };
    const rebindUpdatedBaseline = (intent) => {
      const scope = currentRoot(intent);
      const native = nativeIn(scope);
      if (intent.control.isConnected && intent.control.closest(ROOT) === scope && native === intent.native) return;
      if (intent.phase !== 'BASELINE' || intent.reentered || !(intent.baselineCartUpdated || intent.reviewBenefitsSynchronized) || !quantitiesMatch(scope, intent.baseline, intent)) throw failError('cart_changed');
      const actions = scope.querySelector(ACTIONS);
      const candidates = intent.plugin
        ? [...(actions?.querySelectorAll(intent.mode === 'paid' ? PAID : NORMAL) || [])].filter((node) => node.closest(ROOT) === scope)
        : [native];
      if (candidates.length !== 1) throw failError('cart_changed');
      const control = candidates[0];
      if (intent.mode === 'paid' && quoteText(control) !== intent.displayedQuote) throw failError('cart_changed');
      const form = formOf(native);
      if (!form?.isConnected) throw failError('cart_changed');
      if (!intent.plugin && (form.id !== intent.originalForm.id || native.getAttribute('name') !== intent.native.getAttribute('name') || native.value !== intent.native.value)) throw failError('cart_changed');
      intent.control = control; intent.scope = scope; intent.originalForm = form;
      if (intent.native !== native) bindNative(intent, native);
    };
    const ordinaryMoney = (cart) => {
      const lines = cart.lines.filter((line) => line.productId !== config.insuranceId);
      if (lines.some((line) => !Number.isSafeInteger(line.price) || line.price < 0 || !Number.isSafeInteger(line.finalLinePrice) || line.finalLinePrice < 0)) throw failError('cart_changed');
      const protectedLines = insuranceLines(cart, config.insuranceId);
      if (protectedLines.some((line) => !Number.isSafeInteger(line.finalLinePrice) || line.finalLinePrice < 0)) throw failError('cart_changed');
      const net = cart.total - protectedLines.reduce((sum, line) => sum + line.finalLinePrice, 0);
      if (!Number.isSafeInteger(net) || net < 0) throw failError('cart_changed');
      return stable([cart.currency, net, lines.map((line) => stable([line.productId, line.variantId, line.plan, line.properties, line.quantity, line.price, line.finalLinePrice])).sort()]);
    };
    const repairUnconfirmedPaid = async (intent) => {
      const baseline = intent.baseline;
      const residual = insuranceLines(baseline, config.insuranceId);
      if (baseline.confirm !== '0' || !residual.length) return;
      intent.lastStep = 'paid:clear_unconfirmed'; render('clearing');
      const expectedMoney = ordinaryMoney(baseline);
      const updates = Object.fromEntries(residual.map((line) => [line.key, 0]));
      await request(config.updateUrl, intent, { updates, attributes: { xcsp_confirm: '0' } });
      // Only keys already read as this exact protection resource may be stale
      // in the old UI. Ordinary item keys and quantities still must reconcile.
      intent.cleanedUnconfirmed = true;
      const clean = await settledCart(intent, true);
      if (insuranceLines(clean, config.insuranceId).length || clean.confirm !== '0' || ordinaryMoney(clean) !== expectedMoney) throw failError('cart_changed');
      const scope = currentRoot(intent), currentPaid = scope.querySelector(PAID), currentNative = nativeIn(scope);
      if (!currentPaid || quoteText(currentPaid) !== intent.quote.text) throw failError('cart_changed');
      intent.control = currentPaid; intent.scope = scope;
      if (intent.native !== currentNative) bindNative(intent, currentNative);
      intent.originalForm = formOf(currentNative);
      if (!intent.originalForm?.isConnected) throw failError('cart_changed');
      intent.baseline = clean;
    };
    const start = (scope, control, mode, plugin) => {
      const surface = scope.getAttribute('data-luggex-checkout-safety-surface');
      const intent = { scope, control, surface, mode, plugin, phase: 'BASELINE', controllers: new Set(), disabledUpdates: [], knownInsuranceKeys: new Set() };
      active = intent;
      try {
        if (!config || !['main', 'drawer'].includes(surface)) throw failError('config_error');
        intent.native = nativeIn(scope); intent.originalForm = formOf(intent.native);
        if (!intent.originalForm) throw failError('config_error');
        if (mode === 'paid') intent.displayedQuote = quoteText(control);
        bindNative(intent, intent.native);
        render('checking');
        void (async () => {
          try {
            intent.reviewBenefitsSynchronized = await syncReviewBenefits(intent);
            intent.baseline = await settledCart(intent, false);
            intent.goods = goodsSignature(intent.baseline, config.insuranceId);
            insuranceLines(intent.baseline, config.insuranceId).forEach((line) => intent.knownInsuranceKeys.add(line.key));
            if (plugin || intent.reviewBenefitsSynchronized) rebindUpdatedBaseline(intent);
            if (mode === 'paid') {
              intent.lastStep = 'paid:quote';
              if (config.currency && config.currency !== intent.baseline.currency) throw failError('cart_changed');
              intent.quote = parseDisplayedQuote(intent.displayedQuote, intent.baseline.currency);
              await repairUnconfirmedPaid(intent);
              intent.quoteVariant = await readPublicQuoteVariant(intent);
              if (quoteText(intent.control) !== intent.quote.text) throw failError('cart_changed');
            }
            if (plugin) {
              const currentControl = intent.control;
              if (!currentControl.isConnected || currentControl.closest(ROOT) !== currentRoot(intent)) throw failError('cart_changed');
              intent.phase = 'SDK'; intent.allowPluginClick = currentControl;
              render('waiting');
              intent.watchdog = root.setTimeout(() => fail(intent, 'timeout'), sdkMs);
              currentControl.click();
              intent.allowPluginClick = null;
              intent.reentered = true;
              // Do not change SDK disabled state before its one reentry.
              if (active === intent && !['FAILED', 'LEAVING'].includes(intent.phase)) lockVisibleControls();
            } else await validateFinal(intent);
          } catch (error) { fail(intent, error.code || 'error'); }
        })();
      } catch (error) { fail(intent, error.code || 'config_error'); }
    };
    const handleClick = (event) => {
      const control = event.target?.closest?.(`${NORMAL}, ${PAID}, ${FALLBACK}, [name="checkout"]`);
      const scope = control?.closest(ROOT);
      if (!scope || !enabled(scope)) return false;
      if (active?.phase === 'SDK' && active.allowPluginClick === control) { active.allowPluginClick = null; return true; }
      if (active) {
        if (control === active.native && active.phase === 'SDK') checkpoint(active, event);
        else block(event);
        return true;
      }
      block(event);
      start(scope, control, control.matches(PAID) ? 'paid' : 'normal', control.matches(`${NORMAL}, ${PAID}`));
      return true;
    };
    const handleSubmit = (event) => {
      const form = event.target;
      if (event.submitter?.getAttribute('name') === 'update') return false;
      let scope = event.submitter?.closest?.(ROOT) || form.closest?.(ROOT);
      if (!scope && form.hasAttribute?.('data-luggex-checkout-safety-form')) {
        const surface = form.getAttribute('data-luggex-checkout-safety-surface');
        const candidates = roots().filter((node) => node.getAttribute('data-luggex-checkout-safety-surface') === surface);
        if (candidates.length === 1) scope = candidates[0];
      }
      if (!scope || !enabled(scope)) return false;
      if (acceptPermit(form, event.submitter)) return true;
      block(event);
      if (active?.phase === 'SDK' && form === active.originalForm) void validateFinal(active);
      else if (!active) start(scope, event.submitter || nativeIn(scope), 'normal', false);
      return true;
    };
    const acceptPermit = (form, submitter) => {
      const intent = active;
      if (!intent || intent.phase !== 'COMMIT' || intent.form !== form || intent.submitter !== submitter || intent.permitUsed || !form.isConnected) return false;
      intent.permitUsed = true; intent.phase = 'LEAVING';
      intent.navigationTimer = root.setTimeout(() => fail(intent, 'navigation_failed'), navMs);
      return true;
    };
    const settledDirectCart = async (intent) => {
      let end, previous;
      intent.lastStep = 'direct:stable_cart';
      while (end === undefined || clock() < end) {
        const cart = await request(config.cartJsonUrl, intent);
        if (end === undefined) end = clock() + settleMs;
        const signature = stable([cart.currency, goodsSignature(cart, config.insuranceId), cart.lines.map((line) => [line.key, line.quantity])]);
        if (previous === signature) return cart;
        previous = signature; await delay(350, intent);
      }
      throw failError('cart_changed');
    };
    const runDirectNormal = async (addAction, callbacks = {}) => {
      if (!alive || suspended || active || !config || typeof addAction !== 'function') throw Object.assign(failError('config_error'), { customerMessage: text('config_error'), reloadRequired: true });
      const intent = { mode: 'normal', phase: 'DIRECT_ADD', direct: true, controllers: new Set(), disabledUpdates: [], knownInsuranceKeys: new Set(), onFailure: callbacks.onFailure };
      active = intent;
      render('checking'); lockVisibleControls();
      let addError;
      try {
        await syncReviewBenefits(intent);
        const beforeAdd = await settledDirectCart(intent);
        try { await bounded(Promise.resolve().then(() => addAction()), intent, sdkMs); }
        catch (error) { if (error.code !== 'timeout') addError = error; throw error; }
        await syncReviewBenefits(intent);
        intent.baseline = await settledDirectCart(intent);
        if (beforeAdd.currency !== intent.baseline.currency) throw failError('cart_changed');
        const quantities = (cart) => {
          const grouped = new Map(); cart.lines.filter((line) => line.productId !== config.insuranceId).forEach((line) => { const id = stable([line.productId, line.variantId, line.plan, line.properties]); grouped.set(id, (grouped.get(id) || 0) + line.quantity); }); return grouped;
        };
        const existing = quantities(beforeAdd), after = quantities(intent.baseline);
        if ([...existing].some(([id, quantity]) => (after.get(id) || 0) < quantity)) throw failError('cart_changed');
        if ([...after.values()].reduce((sum, quantity) => sum + quantity, 0) <= [...existing.values()].reduce((sum, quantity) => sum + quantity, 0)) throw failError('cart_changed');
        intent.goods = goodsSignature(intent.baseline, config.insuranceId);
        insuranceLines(intent.baseline, config.insuranceId).forEach((line) => intent.knownInsuranceKeys.add(line.key));
        intent.phase = 'FINAL'; render('clearing');
        const fresh = await request(config.cartJsonUrl, intent); verifyGoods(intent, fresh);
        const updates = Object.fromEntries(insuranceLines(fresh, config.insuranceId).map((line) => { intent.knownInsuranceKeys.add(line.key); return [line.key, 0]; }));
        await request(config.updateUrl, intent, { updates, attributes: { xcsp_confirm: '0' } });
        const verified = await settledDirectCart(intent); verifyGoods(intent, verified);
        if (insuranceLines(verified, config.insuranceId).length) throw failError('cart_changed');
        if (verified.confirm !== '0') throw failError('cart_changed');
        intent.phase = 'LEAVING'; render('opening');
        intent.directResolved = true;
        intent.navigationTimer = root.setTimeout(() => fail(intent, 'navigation_failed'), navMs);
        return { verified: true };
      } catch (error) {
        fail(intent, error.code || 'error');
        const customerMessage = addError?.customerMessage || addError?.message || text(error.code || 'error');
        throw Object.assign(failError(error.code || 'error'), { customerMessage, reloadRequired: true, ...(addError && { cause: addError }) });
      }
    };
    const retryClick = (event) => {
      const button = event.target?.closest?.(RETRY);
      if (!button?.closest(ROOT) || !enabled(button.closest(ROOT))) return;
      event.preventDefault();
      if (config) root.location.assign(config.cartUrl);
      else root.location.reload();
    };
    const readConfig = () => {
      try {
        const nodes = [...document.querySelectorAll('script[type="application/json"][data-luggex-checkout-safety-config]')];
        if (!nodes.length) throw failError('config_error');
        const parsed = nodes.map((node) => validateConfig(JSON.parse(node.textContent), root.location.origin));
        if (parsed.some((candidate) => candidate.insuranceId !== parsed[0].insuranceId || candidate.cartJsonUrl !== parsed[0].cartJsonUrl || candidate.updateUrl !== parsed[0].updateUrl)) throw failError('config_error');
        return parsed[0];
      } catch (_) { return null; }
    };
    const scan = () => {
      if (!alive || suspended) return;
      if (!active) config = readConfig();
      if (!config) render('config_error', true);
      else if (active) {
        if (!active.plugin || active.reentered || active.phase === 'FAILED') lockVisibleControls();
        if (active.phase === 'FAILED') render(active.errorCode || 'error', true);
      }
    };
    const cartUpdated = () => {
      if (active?.phase === 'BASELINE' && !active.reentered) active.baselineCartUpdated = true;
      scan();
    };
    const pageHide = () => {
      suspended = true;
      if (active) {
        root.clearTimeout(active.watchdog); root.clearTimeout(active.navigationTimer);
        active.controllers.forEach((controller) => controller.abort());
        restoreUpdates(active); root.__luggexCheckoutSafetyBootstrap?.cancelPermit?.();
        active.phase = 'EXITED';
      }
    };
    const pageShow = (event) => { const returning = suspended || event?.persisted === true; suspended = false; if (returning && active) fail(active, 'navigation_failed'); else scan(); };
    const sectionUnload = (event) => { if (active?.scope && event.target?.contains?.(active.scope)) fail(active, 'cart_changed'); };
    const destroy = () => {
      if (!alive) return;
      pageHide(); alive = false; restoreLocks();
      listeners.splice(0).forEach((remove) => remove());
      document.removeEventListener('click', retryClick);
      document.removeEventListener('cart:updated', cartUpdated);
      document.removeEventListener('shopify:section:load', scan);
      document.removeEventListener('shopify:section:unload', sectionUnload);
      root.removeEventListener('pagehide', pageHide); root.removeEventListener('pageshow', pageShow);
      if (root[KEY]?.destroy === destroy) delete root[KEY];
    };
    document.addEventListener('click', retryClick);
    document.addEventListener('cart:updated', cartUpdated);
    document.addEventListener('shopify:section:load', scan);
    document.addEventListener('shopify:section:unload', sectionUnload);
    root.addEventListener('pagehide', pageHide); root.addEventListener('pageshow', pageShow);
    scan();
    const api = { ready: true, get configReady() { return !!config; }, get diagnostic() {
      return { phase: active?.phase || 'IDLE', errorPhase: active?.errorPhase || '', errorCode: active?.errorCode || '', lastStep: active?.lastStep || '', mode: active?.mode || '', surface: active?.surface || (active?.direct ? 'direct' : ''),
        readCount: active?.readCount || 0, writeCount: active?.writeCount || 0, requestsActive: active?.controllers?.size || 0, lastRequestMs: active?.lastRequestMs || 0, qtyState: active?.qtyState || '', permitUsed: !!active?.permitUsed, configReady: !!config };
    }, handleClick, handleSubmit, acceptPermit, runDirectNormal, scan, destroy };
    return api;
  }

  const exported = { identifier, cartSnapshot, goodsSignature, insuranceLines, parseDisplayedQuote, validateConfig, createRuntime };
  if (typeof module === 'object' && module.exports) module.exports = exported;
  const root = typeof window === 'undefined' ? null : window;
  if (!root?.document || root[KEY]) return;
  root[KEY] = createRuntime(root);
})();
